import i18next, { type Resource, type TOptions } from 'i18next'
import { useMemo } from 'react'
import {
	Trans as I18nTrans,
	useTranslation as i18nUseTranslation,
	initReactI18next,
	type TransProps
} from 'react-i18next'
import {
	APP_LANGUAGES,
	type AppLanguageCode,
	getInitialAppLanguage
} from './languages'
import type { sharedTranslations } from './translations/shared-translations'
import type { NamespaceMap, TKeyOf } from './types'

interface AppI18nResources<Namespaces extends NamespaceMap> {
	en: Namespaces
	it: Namespaces
}

/**
 * A minimal, live-delegating view over the shared i18next singleton. Only the
 * members the apps and shared UI actually use are exposed, typed to the caller's
 * key union. `language`/`resolvedLanguage` are getters so they reflect the
 * current language after `changeLanguage`, not a snapshot.
 */
function createI18nFacade<TKey extends string>() {
	return {
		get language() {
			return i18next.language
		},
		get resolvedLanguage() {
			return i18next.resolvedLanguage
		},
		exists: (key: TKey, options?: TOptions) => i18next.exists(key, options),
		t: (key: TKey, options?: TOptions) => i18next.t(key, options)
	}
}

function createUseTranslation<TKey extends string>() {
	return () => {
		const translation = i18nUseTranslation()

		/** Manual memo: the React Compiler skips factory-returned functions, and an unmemoized spread breaks memoization in every consumer. */
		return useMemo(
			() => ({
				...translation,
				t: translation.t as (key: TKey, options?: TOptions) => string
			}),
			[translation]
		)
	}
}

function createTrans<TKey extends string, Namespace extends string>() {
	return ({
		i18nKey,
		ns,
		...props
	}: Omit<TransProps<string>, 'i18nKey' | 'ns'> & {
		i18nKey: TKey
		ns?: Namespace
	}) => <I18nTrans {...props} i18nKey={i18nKey as string} ns={ns} />
}

function createTranslateApp<TKey extends string>() {
	return (language: AppLanguageCode, key: TKey, options?: TOptions) =>
		i18next.getFixedT(language)(key, options)
}

/**
 * Initialize the shared i18next instance with an app's composed `resources` and
 * return a fully-typed translation API. Call once per app from the app's
 * `@/lib/i18n` facade. The same singleton backs the components-typed exports
 * below, which `@repo/ui` consumes for shared components.
 */
export function createAppI18n<Namespaces extends NamespaceMap>(
	resources: AppI18nResources<Namespaces> & Resource,
	options: { defaultNS: keyof Namespaces & string }
) {
	const defaultNS = options.defaultNS
	const namespaces = Object.keys(resources.en)

	i18next.use(initReactI18next).init({
		resources,
		ns: namespaces,
		defaultNS,
		fallbackLng: 'en',
		debug: false,
		lng: getInitialAppLanguage(),
		showSupportNotice: false,
		initImmediate: false,
		react: {
			useSuspense: false
		},
		supportedLngs: APP_LANGUAGES.map((language) => language.code),
		interpolation: {
			escapeValue: false
		}
	})

	type TKey = TKeyOf<Namespaces>
	type AppNamespace = keyof Namespaces & string

	return {
		i18n: createI18nFacade<TKey>(),
		useTranslation: createUseTranslation<TKey>(),
		Trans: createTrans<TKey, AppNamespace>(),
		translateApp: createTranslateApp<TKey>()
	}
}

/**
 * Shared-typed API for shared `@repo/ui` components, which may only use keys
 * from the `shared` namespace (present in every app, shipped by this
 * package). Backed by the same singleton initialized by the app's
 * `createAppI18n`. App-only text lives in each app's own `common` namespace.
 */
type SharedNamespaces = Record<'shared', typeof sharedTranslations.en>
export type TKey = TKeyOf<SharedNamespaces>

export const useTranslation = createUseTranslation<TKey>()
export const Trans = createTrans<TKey, 'shared'>()
export const translateApp = createTranslateApp<TKey>()

/**
 * Translate a dynamic key not known at compile time (e.g. breadcrumb keys read
 * from route metadata). Accepts any namespaced key string; the caller is
 * responsible for the key being valid for the running app.
 *
 * **Name `language` from anything that renders.** Without it this reads the
 * singleton, which the React Compiler cannot see: it memoizes the call on
 * `key` and `options` alone, so the string survives a language change and the
 * reader keeps the words of the language they just left (OPSSTD-5458). Pass
 * the `language` from `useTranslation`, or from `useLocaleFormat` in
 * `@repo/ui/hooks/use-locale-format`, which resolves it.
 */
export function translateKey(
	key: string,
	options?: TOptions,
	language?: string
) {
	return language
		? i18next.getFixedT(language)(key, options)
		: i18next.t(key, options)
}

const i18n = createI18nFacade<TKey>()
export default i18n
